const sortable = (element, options) => {
    const { onDragOrder, onUpdateOrder, dragHandle, itemClassName, idleClassName, draggingClassName, dragX } = options;
    
    // Tutorial: https://tahazsh.com/blog/seamless-ui-with-js-drag-to-reorder-example

    /***********************
     *      Variables       *
     ***********************/
    let listContainer;

    let draggableItem;

    let pointerStartX;
    let pointerStartY;

    let items = [];

    let prevRect = {};

    /***********************
     *    Helper Functions   *
     ***********************/

    function getAllItems() {
        if (!items?.length) {
            items = Array.from(listContainer.querySelectorAll(`:scope > .${itemClassName}`));
        }
        return items
    }

    function getIdleItems() {
        return getAllItems().filter((item) => item.classList.contains(`${idleClassName}`));
    }

    function isItemAbove(item) {
        return item.hasAttribute('data-is-above');
    }

    function isItemToggled(item) {
        return item.hasAttribute('data-is-toggled');
    }

    /***********************
     *        Setup        *
     ***********************/

    function setup() {
        listContainer = element;

        if (!listContainer) return;
        
        listContainer.addEventListener('mousedown', dragStart);
        listContainer.addEventListener('touchstart', dragStart);

        document.addEventListener('mouseup', dragEnd);
        document.addEventListener('touchend', dragEnd);
        
        const removeHandlers = () => {
            listContainer.removeEventListener('mousedown', dragStart);
            listContainer.removeEventListener('touchstart', dragStart);

            document.removeEventListener('mouseup', dragEnd);
            document.removeEventListener('touchend', dragEnd);
        }
        
        return removeHandlers;
    }

    /***********************
     *     Drag Start      *
     ***********************/

    function dragStart(e) {
        const element = e.target.closest(`.${dragHandle}`);
        
        if (element) {
            draggableItem = element.closest(`.${itemClassName}`);
        }

        if (!draggableItem) return;

        getAllItems()
        .forEach(element => element.classList.add(`${idleClassName}`));

        pointerStartX = e.clientX || e.touches?.[0]?.clientX;
        pointerStartY = e.clientY || e.touches?.[0]?.clientY;

        disablePageScroll();
        initDraggableItem();
        initItemsState();
        prevRect = draggableItem.getBoundingClientRect();

        document.addEventListener('mousemove', drag);
        document.addEventListener('touchmove', drag, { passive: false });
    }

    function disablePageScroll() {
        document.body.style.overflow = 'hidden';
        document.body.style.touchAction = 'none';
        document.body.style.userSelect = 'none';
    }

    function initItemsState() {
        getIdleItems().forEach((item, i) => {
            if (getAllItems().indexOf(draggableItem) > i) {
                item.dataset.isAbove = '';
            }
        })
    }

    function initDraggableItem() {
        draggableItem.classList.remove(`${idleClassName}`);
        draggableItem.classList.add(`${draggingClassName}`);
    }

    /***********************
     *        Drag         *
     ***********************/

    const getTouches = (e) => {
        if( e.touches ) {
            return e.touches[0];
        } else {
            return {};
        }
    }

    function drag(e) {
        if (!draggableItem) return;

        e.preventDefault();

        const clientX = e.clientX || getTouches(e).clientX;
        const clientY = e.clientY || getTouches(e).clientY;

        let pointerOffsetX = clientX - pointerStartX;
        const pointerOffsetY = clientY - pointerStartY;
        
        if( !dragX ) {
            pointerOffsetX = 0;
        }

        draggableItem.style.transform = `translate(${pointerOffsetX}px, ${pointerOffsetY}px)`;
        updateIdleItemsStateAndPosition();
    }

    function updateIdleItemsStateAndPosition() {
        const draggableItemRect = draggableItem.getBoundingClientRect();
        const draggableItemY = draggableItemRect.top + draggableItemRect.height / 2;

        // Update state
        getIdleItems().forEach((item) => {
            const itemRect = item.getBoundingClientRect();
            const itemY = itemRect.top + itemRect.height / 2;
            if (isItemAbove(item)) {
                if (draggableItemY <= itemY) {
                    item.dataset.isToggled = '';
                } else {
                    delete item.dataset.isToggled;
                }
            } else {
                if (draggableItemY >= itemY) {
                    item.dataset.isToggled = '';
                } else {
                    delete item.dataset.isToggled;
                }
            }
        });

        // Update position
        getIdleItems().forEach((item) => {
            if (isItemToggled(item)) {
                const direction = isItemAbove(item) ? 1 : -1;
                item.style.transform = `translateY(${direction * (draggableItemRect.height)}px)`;
            } else {
                item.style.transform = '';
            }
        });
        
        if( onDragOrder ) {
            onDragOrder( getNewItemOrder() );
        }
        
    }

    /***********************
     *      Drag End       *
     ***********************/

    function dragEnd(e) {
        if (!draggableItem) return;

        applyNewItemsOrder(e);
        cleanup(); 
    }

    function getNewItemOrder() {
        const reorderedItems = [];

        getAllItems().forEach((item, index) => {
            if (item === draggableItem) {
                return;
            }
            if (!isItemToggled(item)) {
                reorderedItems[index] = item;
                return;
            }
            
            const newIndex = isItemAbove(item) ? index + 1 : index - 1;
            reorderedItems[newIndex] = item;
        })

        for (let index = 0; index < getAllItems().length; index++) {
            const item = reorderedItems[index];
            if (typeof item === 'undefined') {
                reorderedItems[index] = draggableItem;
            }
        }
        
        return reorderedItems;
    }

    function applyNewItemsOrder(e) {
        const reorderedItems = getNewItemOrder();
        reorderedItems.forEach((item) => {
            listContainer.appendChild(item);
        });

        draggableItem.style.transform = '';

        requestAnimationFrame(() => {
            const rect = draggableItem.getBoundingClientRect();
            const yDiff = prevRect.y - rect.y;
            const currentPositionX = e.clientX || e.changedTouches?.[0]?.clientX;
            const currentPositionY = e.clientY || e.changedTouches?.[0]?.clientY;

            let pointerOffsetX = currentPositionX - pointerStartX;
            const pointerOffsetY = currentPositionY - pointerStartY;
            
            if( !dragX ) {
                pointerOffsetX = 0;
            }

            draggableItem.style.transform = `translate(${pointerOffsetX}px, ${pointerOffsetY + yDiff}px)`;
            requestAnimationFrame(() => {
                unsetDraggableItem();
            })
        })
    }

    function cleanup() {
        items = [];
        unsetItemState();
        enablePageScroll();

        document.removeEventListener('mousemove', drag);
        document.removeEventListener('touchmove', drag);
    }

    function unsetDraggableItem() {
        draggableItem.style = null;
        draggableItem.classList.remove(`${draggingClassName}`);
        draggableItem.classList.add(`${idleClassName}`);
        draggableItem = null;
        
        if (onUpdateOrder) {
            onUpdateOrder(getNewItemOrder());
        }
    }

    function unsetItemState() {
        getIdleItems().forEach((item, i) => {
            delete item.dataset.isAbove;
            delete item.dataset.isToggled;
            item.style.transform = '';
        })
    }

    function enablePageScroll() {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        document.body.style.userSelect = '';
    }

    return setup();
}

export { sortable };